<script lang="ts" setup>
import { PropType } from 'vue'
import { Editor, Toolbar } from '@wangeditor-next/editor-for-vue'
import { i18nChangeLanguage, IDomEditor, IEditorConfig } from '@wangeditor-next/editor'
import { propTypes } from '@/utils/propTypes'
import { isNumber } from '@/utils/is'
import { ElMessage } from 'element-plus'
import { useLocaleStore } from '@/store/modules/locale'
import { getRefreshToken, getTenantId } from '@/utils/auth'
import { getUploadUrl } from '@/components/UploadFile/src/useUpload'

defineOptions({ name: 'Editor' })

type InsertFnType = (url: string, alt: string, href: string) => void

const localeStore = useLocaleStore()

const currentLocale = computed(() => localeStore.getCurrentLocale)

i18nChangeLanguage(unref(currentLocale).lang)

const props = defineProps({
  editorId: propTypes.string.def('wangEditor-1'),
  height: propTypes.oneOfType([Number, String]).def('500px'),
  editorConfig: {
    type: Object as PropType<Partial<IEditorConfig>>,
    default: () => undefined
  },
  readonly: propTypes.bool.def(false),
  modelValue: propTypes.string.def(''),
  maxlength: propTypes.number.def(0)
})

const emit = defineEmits(['change', 'update:modelValue'])

const editorRef = shallowRef<IDomEditor>()

const valueHtml = ref('')

const remainingLength = computed(() => {
  if (!props.maxlength) return null
  const htmlLength = unref(valueHtml).length
  return htmlLength
})

const isOverLimit = computed(() => {
  if (!props.maxlength) return false
  const htmlLength = unref(valueHtml).length
  return htmlLength > props.maxlength
})

function stripHtmlTags(html: string): string {
  const tmp = document.createElement('div')
  tmp.innerHTML = html
  return tmp.innerText || ''
}

watch(
  () => props.modelValue,
  (val: string) => {
    if (!val) {
      val = ''
    }
    if (val === unref(valueHtml)) return
    valueHtml.value = val
  },
  {
    immediate: true
  }
)

watch(
  () => valueHtml.value,
  (val: string) => {
    emit('update:modelValue', val)
  }
)

watch(
  () => props.readonly,
  (val) => {
    if (val) {
      editorRef.value?.disable()
    } else {
      editorRef.value?.enable()
    }
  }
)

const handleCreated = (editor: IDomEditor) => {
  editorRef.value = editor
}

const editorConfig = computed((): IEditorConfig => {
  return Object.assign(
    {
      placeholder: '请输入内容...',
      readOnly: props.readonly,
      customAlert: (s: string, t: string) => {
        switch (t) {
          case 'success':
            ElMessage.success(s)
            break
          case 'info':
            ElMessage.info(s)
            break
          case 'warning':
            ElMessage.warning(s)
            break
          case 'error':
            ElMessage.error(s)
            break
          default:
            ElMessage.info(s)
            break
        }
      },
      autoFocus: false,
      scroll: true,
      EXTEND_CONF: {
        mentionConfig: {
          showModal: () => {},
          hideModal: () => {}
        }
      },
      MENU_CONF: {
        ['uploadImage']: {
          server: getUploadUrl(),
          maxFileSize: 5 * 1024 * 1024,
          maxNumberOfFiles: 10,
          allowedFileTypes: ['image/*'],
          headers: {
            Accept: '*',
            Authorization: 'Bearer ' + getRefreshToken(),
            'tenant-id': getTenantId()
          },
          timeout: 15 * 1000,
          fieldName: 'file',
          onBeforeUpload(file: File) {
            return file
          },
          onProgress(progress: number) {
            console.log('progress', progress)
          },
          onSuccess(file: File, res: any) {
            console.log('onSuccess', file, res)
          },
          onFailed(file: File, res: any) {
            alert(res.message)
            console.log('onFailed', file, res)
          },
          onError(file: File, err: any, res: any) {
            alert(err.message)
            console.error('onError', file, err, res)
          },
          customInsert(res: any, insertFn: InsertFnType) {
            insertFn(res.data, 'image', res.data)
          }
        },
        ['uploadVideo']: {
          server: getUploadUrl(),
          maxFileSize: 10 * 1024 * 1024,
          maxNumberOfFiles: 10,
          allowedFileTypes: ['video/*'],
          headers: {
            Accept: '*',
            Authorization: 'Bearer ' + getRefreshToken(),
            'tenant-id': getTenantId()
          },
          timeout: 15 * 1000,
          fieldName: 'file',
          onBeforeUpload(file: File) {
            return file
          },
          onProgress(progress: number) {
            console.log('progress', progress)
          },
          onSuccess(file: File, res: any) {
            console.log('onSuccess', file, res)
          },
          onFailed(file: File, res: any) {
            alert(res.message)
            console.log('onFailed', file, res)
          },
          onError(file: File, err: any, res: any) {
            alert(err.message)
            console.error('onError', file, err, res)
          },
          customInsert(res: any, insertFn: InsertFnType) {
            insertFn(res.data, 'mp4', res.data)
          }
        }
      },
      uploadImgShowBase64: true
    },
    props.editorConfig || {}
  )
})

const editorStyle = computed(() => {
  return {
    height: isNumber(props.height) ? `${props.height}px` : props.height
  }
})

const handleChange = (editor: IDomEditor) => {
  if (props.maxlength) {
    const htmlLength = editor.getHtml().length
  }
  emit('change', editor)
}

onBeforeUnmount(() => {
  const editor = unref(editorRef.value)
  editor?.destroy()
})

const getEditorRef = async (): Promise<IDomEditor> => {
  await nextTick()
  return unref(editorRef.value) as IDomEditor
}

defineExpose({
  getEditorRef
})
</script>

<template>
  <div class="border-1 border-solid border-[var(--el-border-color)] p-2px rounded-8px z-10">
    <Toolbar
      :editor="editorRef"
      :editorId="editorId"
      class="border-0 b-b-1 border-solid border-[var(--el-border-color)]"
    />
    
    <Editor
      v-model="valueHtml"
      :defaultConfig="editorConfig"
      :editorId="editorId"
      :style="editorStyle"
      @on-change="handleChange"
      @on-created="handleCreated"
    />
    <div
      v-if="maxlength"
      class="pr-16px py-4px flex justify-end items-center text-12px bg-white line-height-none"
      :class="isOverLimit ? 'text-red-500' : 'text-gray-400'"
    >
      <span>{{ remainingLength }}/{{ maxlength }}</span>
    </div>
  </div>
</template>

<style src="@wangeditor-next/editor/dist/css/style.css"></style>

<style scoped></style>
